import { CenterSpinner } from '@/components/ats';
import { PDFReaderBrowser } from '@/components/file-viewers/reactPdf';
import { KText } from '@/ui-library/components';
import { Box, Button, Flex, Text } from '@chakra-ui/react';
import { useEffect, useLayoutEffect, useState, useRef } from 'react';
import { useGenerateOfferDocumentMutation, offerDetailsAPI } from '../../api';
import { useShowToast } from '@/hooks';
import { useKFlags } from '@/hooks/use-k-flags';
import { useOfferStore } from '../../store';
interface DocumentPreviewProps {
  offerId: string;
  templateId: string | null;
  hasValidationIssues: boolean;
  onRegenerateTrigger: () => void;
  regenerateKey: number;
  isTemplateSelectionComplete?: boolean;
  isMultiDoc?: boolean;
  selectedDocIndex?: number;
  // Wires the viewer's built-in "Replace" control to the offer's replace flow.
  onReplace?: (_files: File[]) => void;
  fileName?: string;
  showReplace?: boolean;
  onGenerationError?: (_docIndex: number) => void;
  toolbarLeftControl?: React.ReactNode;
  // Fires true once the PDF preview (with its own toolbar) is on screen.
  onPreviewReady?: (_isReady: boolean) => void;
}

interface OfferDocumentPdf {
  url: string;
  content_type: string;
}

interface OfferData {
  offer_status: string;
  offer_document_pdf?: OfferDocumentPdf;
  // Per-document PDFs (multi-doc), index-aligned with the template's offer_documents tabs.
  offer_documents_pdf?: OfferDocumentPdf[];
}

// Manual polling function matching pollOfferWithRetries from offerFormHelpers
const pollOfferWithRetries = async (
  offerId: string,
  signal: AbortSignal,
  maxRetries = 15,
  timeout = 3000
): Promise<OfferData> => {
  let retries = 0;
  while (retries < maxRetries) {
    if (signal.aborted) throw new Error('Aborted');
    try {
      const response = await offerDetailsAPI.pollOfferById(offerId);
      if (response) {
        const status = response.offer_status;
        if (status === 'document_generated' || status === 'approved_internal') {
          return response;
        } else if (status === 'document_generation_error') {
          throw new Error('Document generation error');
        } else if (
          status !== 'pending_document_generation' &&
          status !== 'document_generation_in_progress'
        ) {
          throw new Error('Unexpected status');
        }
      }
    } catch (err: any) {
      if (
        err.message === 'Document generation error' ||
        err.message === 'Unexpected status' ||
        err.message === 'Aborted' ||
        retries === maxRetries - 1
      ) {
        throw err;
      }
    }
    retries += 1;
    await new Promise<void>((res, rej) => {
      const t = setTimeout(res, timeout);
      signal.addEventListener(
        'abort',
        () => {
          clearTimeout(t);
          rej(new Error('Aborted'));
        },
        { once: true }
      );
    });
  }
  throw new Error('Maximum retries reached');
};

export const DocumentPreview = ({
  offerId,
  templateId,
  hasValidationIssues,
  onRegenerateTrigger,
  regenerateKey,
  isTemplateSelectionComplete = true,
  isMultiDoc: _isMultiDoc = false,
  selectedDocIndex = 0,
  onReplace,
  fileName,
  showReplace = false,
  onGenerationError,
  toolbarLeftControl,
  onPreviewReady,
}: DocumentPreviewProps) => {
  const { tOfferTemplateMultiDocuments: isCompact } = useKFlags();
  const isPolling = useOfferStore((state) => state.isPolling);
  const setIsPolling = useOfferStore((state) => state.setIsPolling);
  const setEnableNextButton = useOfferStore(
    (state) => state.setEnableNextButton
  );
  const [offerData, setOfferData] = useState<OfferData | null>(null);
  const [containerHeight, setContainerHeight] = useState<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);
  // Identity of the in-flight generation; blocks duplicate runs for the same
  // template while a generation is still pending (multi-doc gate oscillation).
  const inFlightRef = useRef<string | null>(null);
  // Tracks the last successfully-completed run key so tab switches that flip
  // hasValidationIssues don't restart a generation that already finished.
  const completedKeyRef = useRef<string | null>(null);
  const isMountedRef = useRef(true);
  // Aborts the in-flight generateAndPoll when a new run supersedes it (template change).
  const abortRef = useRef<AbortController | null>(null);
  const generateDocument = useGenerateOfferDocumentMutation();
  const toast = useShowToast();

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      abortRef.current?.abort();
    };
  }, []);

  const offerStatus = offerData?.offer_status;
  // Prefer per-doc PDFs when the offer was generated as multi-doc (offer_documents_pdf
  // is present), regardless of whether the FE knows it's multi-doc yet — the template
  // list API may omit offer_documents so isMultiDoc can be false even for multi-doc offers.
  const selectedPdf = offerData?.offer_documents_pdf?.length
    ? offerData.offer_documents_pdf[selectedDocIndex]
    : offerData?.offer_document_pdf;
  const pdfUrl = selectedPdf?.url || '';
  const contentType = selectedPdf?.content_type || '';

  // Combined effect: Generate document and poll if needed
  useEffect(() => {
    if (
      !offerId ||
      !templateId ||
      hasValidationIssues ||
      !isTemplateSelectionComplete
    ) {
      return;
    }

    // Skip if a generation for this same identity is already in-flight or has
    // already completed; the multi-doc gate can flip hasValidationIssues across
    // renders (tab switch) and would otherwise restart a finished generation.
    const runKey = `${offerId}:${templateId}:${regenerateKey}`;
    if (inFlightRef.current === runKey) return;
    if (completedKeyRef.current === runKey) return;

    // Abort the previous run so its result can't overwrite state for the new template.
    abortRef.current?.abort();
    const abort = new AbortController();
    abortRef.current = abort;
    inFlightRef.current = runKey;

    const generateAndPoll = async () => {
      setIsPolling(true);
      try {
        // Step 1: Generate document
        const result = await generateDocument.mutateAsync({
          id: offerId,
          event: 'generate_document',
        });

        if (!isMountedRef.current || abort.signal.aborted) return;

        const status = result?.offer_status;

        // Step 2: Poll if BE is still generating (either pending or in-progress state).
        if (
          status === 'pending_document_generation' ||
          status === 'document_generation_in_progress'
        ) {
          const polledData = await pollOfferWithRetries(offerId, abort.signal);
          if (isMountedRef.current && !abort.signal.aborted) {
            setOfferData(polledData);
            completedKeyRef.current = runKey;
          }
        } else {
          if (isMountedRef.current && !abort.signal.aborted) {
            setOfferData(result);
            completedKeyRef.current = runKey;
          }
        }
      } catch (err: any) {
        if (!isMountedRef.current || abort.signal.aborted) return;

        toast({
          title: 'Error generating offer',
          description: err.message || 'Failed to generate offer document',
          status: 'error',
        });
        setOfferData({
          offer_status: 'document_generation_error',
        });
        onGenerationError?.(selectedDocIndex);
      } finally {
        // If we still own the ref, no newer run superseded us — clear isPolling regardless
        // of whether the component is still mounted (unmount-while-generating must also clear).
        const wasOwner = inFlightRef.current === runKey;
        if (wasOwner) {
          inFlightRef.current = null;
          setIsPolling(false);
        }
      }
    };

    generateAndPoll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    offerId,
    templateId,
    regenerateKey,
    hasValidationIssues,
    isTemplateSelectionComplete,
  ]);

  const isGenerating =
    generateDocument.isPending ||
    isPolling ||
    offerStatus === 'pending_document_generation' ||
    offerStatus === 'document_generation_in_progress';

  const showPreview =
    pdfUrl &&
    !isGenerating &&
    !hasValidationIssues &&
    offerStatus !== 'document_generation_error';

  const showError =
    !isGenerating && offerStatus === 'document_generation_error';

  // Layout effect so the parent toggles its fallback toolbar before paint (no flash of both).
  useLayoutEffect(() => {
    onPreviewReady?.(!!showPreview);
  }, [showPreview, onPreviewReady]);

  useEffect(() => {
    if (isGenerating || showError) {
      setEnableNextButton(false);
    } else {
      setEnableNextButton(true);
    }
  }, [isGenerating, setEnableNextButton, showError]);

  // Measure container height
  useEffect(() => {
    const updateHeight = () => {
      if (containerRef.current) {
        const height = containerRef.current.offsetHeight;
        setContainerHeight(height);
      }
    };

    updateHeight();

    // Update height on window resize
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, [showPreview]);

  // Don't render anything if there are validation issues
  if (hasValidationIssues) {
    return null;
  }

  return (
    <Box h="calc(100% - 120px)" w="100%">
      {/* Loading State */}
      {isGenerating && (
        <Flex
          mt="12px"
          direction="column"
          align="center"
          justify="center"
          minH="400px"
          border={isCompact ? undefined : '1px solid'}
          borderColor={isCompact ? undefined : 'border-color'}
          borderRadius="sm"
        >
          <CenterSpinner h="100%" />
          <KText mt="12px" color="secondary-600">
            Generating offer document...
          </KText>
        </Flex>
      )}

      {/* Error State */}
      {showError && (
        <Flex
          mt="24px"
          direction="column"
          align="center"
          justify="center"
          minH="400px"
          border={isCompact ? undefined : '1px solid'}
          borderColor={isCompact ? undefined : 'border-color'}
          borderRadius="sm"
        >
          <Text mb={4}>Document generation failed</Text>
          <Button variant="primary" onClick={onRegenerateTrigger}>
            Re-generate Offer
          </Button>
        </Flex>
      )}

      {/* PDF Preview */}
      {showPreview && (
        <Flex direction="column" gap="12px" h="100%">
          <Box
            ref={containerRef}
            overflow="hidden"
            h="100%"
            minH="300px"
            className="document-preview"
          >
            <PDFReaderBrowser
              file={pdfUrl}
              content_type={contentType}
              preview_url={null}
              showReplaceControl={!!showReplace}
              handleUpload={onReplace}
              acceptedFiles={['.docx']}
              maxFiles={1}
              fileName={fileName}
              toolbarLeftControl={toolbarLeftControl}
              isCompact={isCompact}
              containerStyles={{
                h:
                  containerHeight > 0
                    ? `${containerHeight}px`
                    : 'calc(100% - 400px)',
              }}
            />
          </Box>
        </Flex>
      )}
    </Box>
  );
};
